// Asset ID Creator screens. Pure: state in, HTML out.
// Words: ASSET_IDS_SECTION_WORDS.md rev 3.2 (approved 2026-10-21, 9:02 PM), ASSET_ID_SHEET_REV5_WORDS.md (2026-10-22),
// ASSET_ID_SHEET_REV6_WORDS.md (approved 2026-10-23, 5:32 AM).
// Mockups: MOCKUP_ASSET_ID_CREATOR.html, MOCKUP_ASSET_ID_SHEET_REV5.html, MOCKUP_ASSET_ID_SHEET_REV6.html.
// The sheet is laid out like Field Asset ID Creator.xlsx, tab Field Risk Assessment Form: one row per asset,
// its columns, header words, yellow input headers, the Select / Text / Auto row, and the RPN colors.
// Actions use data-ai so they never collide with the survey's data-sv or the Map's data-mp.

import { assetHref } from "./asset-page";
import { esc } from "./training-screens";
import {
  CONDUCTORS,
  isOutside,
  LOCATIONS,
  padPart,
  rpnBand,
  shockOf,
  VOLTAGES,
  ANSWER_FIELDS,
  TAG_MAX,
  FIELD_DEFINITIONS,
  type ClassOption,
  type DefinedField,
  type IdForm,
} from "./asset-id";

export type RowStatus = "saved" | "wait" | "draft" | "bad";

/** One row of the sheet. The new row (from Copy Down or the blank row) has no saved Asset ID yet. */
export interface SheetRow {
  /** Stable key: the Asset ID, "new" for the new row, or "queue-N" for a Copy Down To row still to come. */
  key: string;
  form: IdForm;
  assetId: string;
  status: RowStatus;
  /** Saved: the cells that make the Asset ID are locked. */
  saved: boolean;
  removed: boolean;
  /** The new row's Asset ID is taken. */
  dupe: boolean;
  /** Picked rows show dropdowns and Y / N buttons. The rest show their values, so a big site stays quick. */
  picked: boolean;
  /** The row's number down the left, as the workbook's row numbers. */
  n: number;
  /** The cell Copy Down stepped: a red border says check it (rev 5). */
  check?: keyof IdForm | null;
  /** A Copy Down To row waiting its turn: shown, not yet editable. */
  queued?: boolean;
  /** A saved row whose Asset ID can still be retyped: label not printed, not on the map. */
  idEditable?: boolean;
}

export interface SheetView {
  siteNumber: string;
  siteName: string;
  /** Build Folder or Drive Folder next to the site name (drive-screens.ts). Empty while Drive is not connected. */
  driveChip?: string;
  siteIdent: string;
  auditor: string;
  initialRa: string;
  online: boolean;
  find: string;
  canMap: boolean;
  fromMap: boolean;
  notPrinted: number;
  packBusy: boolean;
  classes: readonly ClassOption[];
  rows: SheetRow[];
  problems: { assetId: string; text: string }[];
  /** Show Calculations: the math columns, kept to the right. Off by default (rev 5). */
  calcs: boolean;
  /** The row number held: its small menu. */
  menu: { key: string; label: string; draft: boolean; removed: boolean; walk?: boolean } | null;
  /** The thin line under the bar. Shows only when something needs you (rev 6 section 4). */
  line: { text: string; tone: "amber" | "red" } | null;
  canDownTo: boolean;
  showRemoved: boolean;
  /** Aa: the sheet's size, 1 down to 0.7 (rev 6 section 5). */
  zoom: number;
  /** A list cell tapped: its list, under the cell. */
  list: { key: string; cell: keyof IdForm; title: string; values: readonly string[]; current: string; clear: boolean; left: number; top: number } | null;
  /** A column header tapped: its definition card, under the header. */
  def: { field: DefinedField; left: number; top: number } | null;
  isAdmin: boolean;
}

/**
 * The one tight bar above the sheet (rev 6, 2026-10-23), in three groups (Robert, 2026-10-27, 2:38 to 2:42 PM,
 * ASSET_ID_TAG_COLUMN_WORDS.md): where you are and the connection on the left, the row tools in the middle,
 * the label work on the right, ending on Print QR Labels. No button changed what it does.
 */
function bar(v: SheetView): string {
  return `<div class="ac-bar">
    <div class="ac-crumbs"><a href="#/sites/${esc(v.siteNumber)}">${esc(v.siteName)}</a></div>
    ${v.fromMap && v.canMap ? `<a class="ac-tbtn" href="#/survey/${esc(v.siteNumber)}/map">Back To Map</a>` : ""}
    <span class="ac-net"><span class="sv-dot ${v.online ? "on" : ""}"></span>${v.online ? "Online" : "Offline"}</span>
    <button class="ac-tbtn" type="button" data-sv="outbox">Outbox</button>
    <button class="ac-tbtn" type="button" data-ai="refresh"${v.online && !v.packBusy ? "" : " disabled"}>${v.packBusy ? "Refreshing…" : "Refresh Pack"}</button>
    ${v.driveChip ?? ""}
    <span class="ac-gap"></span>
    <input class="ac-find" id="ac-find" data-ai-find value="${esc(v.find)}" placeholder="Find Asset ID" autocomplete="off" autocapitalize="characters" spellcheck="false">
    <button class="ac-tbtn" type="button" data-ai="down-to"${v.canDownTo ? "" : " disabled"}>Copy Down To</button>
    <button class="ac-tbtn${v.calcs ? " on" : ""}" type="button" data-ai="calcs">${v.calcs ? "Hide Calcs" : "Show Calcs"}</button>
    <button class="ac-tbtn ac-aa" type="button" data-ai="aa">Aa<small>${Math.round(v.zoom * 100)}%</small></button>
    <span class="ac-gap"></span>
    <a class="ac-tbtn" href="#/assets/${esc(v.siteNumber)}/labels">Field Labels</a>
    <button class="ac-tbtn dark" type="button" data-ai="print">Print QR Labels · ${v.notPrinted}</button>
  </div>${v.line ? `<div class="ac-line ${v.line.tone}" id="ac-line">${esc(v.line.text)}</div>` : ""}`;
}

/**
 * Rev 5 field order (ASSET_ID_SHEET_REV5_WORDS.md section 9): what you fill in first, on one iPad screen.
 * Location, Loc #, Type, Equip #, END, Asset ID, Voltage, Conductor, the four answers, Out of Service,
 * Residual RPN. The math sits behind Show Calculations. The workbook and records keep the workbook's order.
 */
function head(v: SheetView): string {
  // A header with a definition takes a tap; its hint cell carries a small ⓘ (field definitions, 2026-10-23).
  const df = (field?: DefinedField) => (field ? ` data-ai="def" data-def="${field}"` : "");
  const th = (text: string, w: number, cls = "", field?: DefinedField) => `<th class="ac-hd ${cls}${field ? " ac-df" : ""}" style="min-width:${w}px"${df(field)}>${text}</th>`;
  const hint = (kind: "sel" | "txt" | "auto" | "tap", cls = "", field?: DefinedField) =>
    `<th class="ac-hint ${kind === "tap" ? "sel" : kind} ${cls}${field ? " ac-df" : ""}"${df(field)}>${kind === "sel" ? "🔽" : kind === "txt" ? "✏️" : kind === "tap" ? "Tap" : "🔁"}${field ? " ⓘ" : ""}</th>`;
  return `<thead>
    <tr class="ac-r1"><th class="ac-gut ac-band"></th>
      <th class="ac-band ac-big" colspan="6">Mapping Field Asset Section</th>
      <th class="ac-band ac-aid">Asset ID</th>
      <th class="ac-band ac-big" colspan="7">Field Assessment</th>
      <th class="ac-band ac-sm ac-calc" colspan="7">Calculations · <span class="ac-legend"><span class="low">LOW</span><span class="med">MEDIUM</span><span class="high">HIGH</span></span></th>
      <th class="ac-band ac-sm">${v.initialRa ? `Initial RA<br>${esc(v.initialRa)}` : ""}</th></tr>
    <tr class="ac-r2"><th class="ac-gut">Row</th>
      ${th("Sites Equipment Tag", 128)}${th("Equipment Location (EEL)", 112, "in")}${th("Loc #", 46)}${th("Equipment Type (EET)", 132, "in")}${th("Equip #", 52, "in")}${th('"END" Location', 58, "in bold")}
      ${th("Asset ID for QR Code", 150, "ac-aid")}
      ${th("Nominal System Voltage?", 90, "", "volt")}${th("Conductor", 78, "", "cond")}
      ${th("Passive Exposure?", 66, "sm", "passive")}${th("Active Exposure?", 66, "sm", "active")}${th("Properly Installed?", 66, "sm", "installed")}${th("Used Per Instructions?", 66, "sm", "usedPer")}
      ${th("Out of Service", 56, "in bold sm", "oos")}
      ${th("Shock Hazard?", 30, "v ac-calc")}${th("Likelihood", 30, "v ac-calc")}${th("Severity", 30, "v ac-calc")}${th("RPN (Before Controls)", 34, "v bold ac-calc")}${th("Limited Approach (LAB)", 34, "v ac-calc")}${th("Restricted Approach Boundary (RAB)", 34, "v ac-calc")}${th("Shock Protection Required", 34, "v ac-calc")}
      ${th("Residual RPN", 56, "bold sm")}</tr>
    <tr class="ac-r3"><th class="ac-gut ac-hint auto"></th>
      ${hint("txt")}${hint("sel")}${hint("txt")}${hint("sel")}${hint("txt")}${hint("txt")}${hint("auto", "ac-aid")}
      ${hint("sel", "", "volt")}${hint("sel", "", "cond")}${hint("tap", "", "passive")}${hint("tap", "", "active")}${hint("tap", "", "installed")}${hint("tap", "", "usedPer")}${hint("sel", "", "oos")}
      ${hint("auto", "ac-calc")}${hint("auto", "ac-calc")}${hint("auto", "ac-calc")}${hint("auto", "ac-calc")}${hint("auto", "ac-calc")}${hint("auto", "ac-calc")}${hint("auto", "ac-calc")}${hint("auto")}</tr>
  </thead>`;
}

/** Conductor shows Fixed or Moveable, so the column stays narrow. The record keeps the full words. */
const shown = (key: keyof IdForm, o: string) => (key === "cond" ? o.replace(" Conductor", "") : o);

/**
 * A list cell. On the picked row (and the new row) it opens SafeTag's own list under the cell on one tap.
 * Not the iPad wheel: the wheel opens on the first tap and would eat the second tap of a double-tap (rev 6).
 */
function cellSelect(r: SheetRow, key: keyof IdForm, locked: boolean): string {
  const v = r.form[key];
  const need = !r.saved && !v;
  const chk = r.check === key ? " check" : "";
  if (locked || !r.picked) return `<td class="ac-val${locked ? " lock" : ""}${need ? " need" : ""}${chk}" data-ai="pick" data-key="${esc(r.key)}">${esc(shown(key, v))}</td>`;
  return `<td class="ac-dd${need ? " need" : ""}${chk}" data-ai="cell" data-key="${esc(r.key)}" data-cell="${key}">${esc(shown(key, v))}</td>`;
}

function cellText(r: SheetRow, key: "locNum" | "num" | "pos", locked: boolean, needed: boolean): string {
  const v = r.form[key];
  const text = key === "locNum" ? v : padPart(v) || v;
  const need = needed && !r.saved && !v;
  const chk = r.check === key ? " check" : "";
  if (locked || !r.picked) return `<td class="ac-val big${locked ? " lock" : ""}${need ? " need" : ""}${chk}" data-ai="pick" data-key="${esc(r.key)}">${esc(text)}</td>`;
  return `<td class="${`${need ? "need" : ""}${chk}`.trim()}"><input data-ai-cell="${key}" data-key="${esc(r.key)}" id="ac-${key}-${esc(r.key)}" value="${esc(v)}" autocomplete="off" autocapitalize="characters" spellcheck="false" inputmode="${key === "locNum" ? "numeric" : "text"}" enterkeyhint="done"></td>`;
}

/**
 * Sites Equipment Tag (ASSET_ID_TAG_COLUMN_WORDS.md): the plant's own name, typed as the door or the study reads it.
 * Not part of the ID, so it stays open on a saved row, like the shock answers. Never required.
 */
function cellTag(r: SheetRow, closed: boolean): string {
  const v = r.form.tag ?? "";
  if (closed || !r.picked) return `<td class="ac-val ac-tag" data-ai="pick" data-key="${esc(r.key)}">${esc(v)}</td>`;
  return `<td class="ac-tag"><input data-ai-tag data-key="${esc(r.key)}" id="ac-tag-${esc(r.key)}" value="${esc(v)}" maxlength="${TAG_MAX}" autocomplete="off" autocapitalize="characters" spellcheck="false" enterkeyhint="done"></td>`;
}

/** Passive, Active, Installed, Used Per: Y and N buttons on the picked row. One tap each, no dropdown. */
function cellYesNo(r: SheetRow, key: (typeof ANSWER_FIELDS)[number]): string {
  const v = r.form[key];
  // Active YES sets Passive YES and locks it: credible is always conceivable (2026-10-23, 6:37 AM).
  if (key === "passive" && r.form.active === "YES") return `<td class="ac-val ac-follow" data-ai="pick" data-key="${esc(r.key)}">Y</td>`;
  const need = !r.saved && !v;
  if (r.removed || !r.picked) return `<td class="ac-val${need ? " need" : ""}" data-ai="pick" data-key="${esc(r.key)}">${esc(v)}</td>`;
  const b = (ans: "YES" | "NO") => `<button type="button" class="${ans === "YES" ? "y" : "n"}${v === ans ? " on" : ""}" data-ai="yn" data-key="${esc(r.key)}" data-cell="${key}" data-v="${ans}">${ans === "YES" ? "Y" : "N"}</button>`;
  return `<td class="ac-yn${need ? " need" : ""}">${b("YES")}${b("NO")}</td>`;
}

const auto = (r: SheetRow, v: string | number | null, cls = "") => `<td class="ac-auto ${cls}" data-ai="pick" data-key="${esc(r.key)}">${esc(v === null ? "---" : v)}</td>`;

function row(v: SheetView, r: SheetRow): string {
  const f = r.form;
  const s = shockOf(f);
  const out = isOutside(f);
  const lock = (r.saved && !r.idEditable) || Boolean(r.queued);
  const dash = (x: string) => x || "---";
  const done = !r.queued && Boolean(f.volt && f.passive && f.active);
  // The new row always takes taps on its cells, picked or not, so a double-tap lands the first time.
  const q = r.queued ? { ...r, picked: false } : r.key === "new" ? { ...r, picked: true } : r;
  const fillable = r.key === "new" && !r.assetId;
  return `<tr class="${r.picked ? "pick" : ""}${!r.saved ? " newrow" : ""}${r.queued ? " queued" : ""}${r.removed ? " gone" : ""}" data-row="${esc(r.key)}">
    <td class="ac-gut" data-ai="${r.queued ? "pick" : "row"}" data-key="${esc(r.key)}"${r.queued ? "" : ' title="Double-tap to add a row under it. Press and hold for the menu."'}>${r.saved ? r.n : r.queued ? "Next" : "New"}<span class="ac-st ${r.status}"></span></td>
    ${cellTag(q, r.removed)}
    ${cellSelect(q, "loc", lock)}
    ${out ? auto(r, "") : cellText(q, "locNum", lock, true)}
    ${cellSelect(q, "type", lock)}
    ${cellText(q, "num", lock, true)}
    ${out ? auto(r, "") : cellText(q, "pos", lock, false)}
    <td class="ac-aid${r.dupe ? " dupe" : ""}" data-ai="${fillable ? "fill-row" : "pick"}" data-key="${esc(r.key)}">${fillable ? '<span class="ac-ph">Double-tap to fill</span>' : esc(r.assetId)}${r.dupe ? `<small>Already exists <button type="button" data-ai="goto" data-id="${esc(r.assetId)}">Open It</button></small>` : ""}${r.removed ? "<small>Removed</small>" : ""}</td>
    ${cellSelect(q, "volt", r.removed)}${cellSelect(q, "cond", r.removed)}
    ${cellYesNo(q, "passive")}${cellYesNo(q, "active")}${cellYesNo(q, "installed")}${cellYesNo(q, "usedPer")}
    ${cellSelect(q, "oos", r.removed)}
    ${auto(r, dash(s.hazard), "ac-calc")}${auto(r, s.likelihood, "ac-calc")}${auto(r, s.severity, "ac-calc")}${auto(r, s.rpn, `${rpnBand(s.rpn)} ac-calc`)}${auto(r, dash(s.lab), "ac-calc")}${auto(r, dash(s.rab), "ac-calc")}${auto(r, dash(s.protection), "ac-calc")}
    ${auto(r, done ? s.residual : "", done ? rpnBand(s.residual) : "")}
  </tr>`;
}

/** "ER2 · MCC-01" from 00-ER2-MCC-01-A3: where the row lives in Field Labels. */
function walkPlace(id: string): string {
  const p = id.split("-");
  if (/^E[RA]\d/.test(p[1] ?? "")) return `${p[1]} · ${p.slice(2, 4).join("-")}`;
  return `Outside · ${p.slice(1, 3).join("-")}`;
}

/** The row number's menu (press and hold). Remove for a saved row, Clear for the new row, Restore for a removed one. */
function rowMenu(m: NonNullable<SheetView["menu"]>, showRemoved: boolean): string {
  // A row from Field Labels: the walk removes and restores it (ASSESSMENT_FLOW_B7_PART2_WORDS.md items 8 and 9).
  const labels = `<a class="ac-menua" href="#/assets/${esc(m.key.slice(0, 2))}/labels">Open Field Labels<small>${esc(walkPlace(m.key))}</small></a>`;
  const item = m.walk && m.removed
    ? `<button type="button" class="off" disabled>Restore Row<small>Removed in Field Labels. Put it back there, then Update Push. The push restores this row with its answers.</small></button>${labels}`
    : m.walk && !m.draft
      ? `<button type="button" class="off" disabled>Remove Row<small>This came from Field Labels. Remove it there, then Update Push.</small></button>${labels}`
      : m.removed
    ? `<button type="button" data-ai="restore" data-key="${esc(m.key)}">Restore Row</button>`
    : m.draft
      ? `<button type="button" class="del" data-ai="clear-new">Clear The New Row<small>It was never saved</small></button>`
      : `<button type="button" class="del" data-ai="remove-ask" data-key="${esc(m.key)}">Remove Row</button>`;
  // One asset page (ONE_ASSET_PAGE_WORDS.md): a saved row opens its page.
  const open = m.draft ? "" : `<a class="ac-menua" href="${assetHref(m.key.slice(0, 2), m.key)}">Open Asset</a>`;
  return `<div class="ai-sheet ac-menuwrap" data-ai-sheet><div class="ac-menu"><div class="ac-menuhd">${esc(m.label)}</div>${open}${item}<button type="button" data-ai="show-removed">${showRemoved ? "Hide Removed Rows" : "Show Removed Rows"}</button><button type="button" data-ai="close">Close</button></div></div>`;
}

/** The list for a list cell, under the cell. Tap outside to close. */
function listPop(l: NonNullable<SheetView["list"]>): string {
  const items = l.values.map((o) => `<button type="button" class="${o === l.current ? "cur" : ""}" data-ai="list-pick" data-v="${esc(o)}">${esc(shown(l.cell, o))}</button>`).join("");
  return `<div class="ai-sheet ac-listwrap" data-ai-sheet><div class="ac-list" style="left:${Math.round(l.left)}px;top:${Math.round(l.top)}px"><div class="ac-menuhd">${esc(l.title)}</div>${items}${l.clear ? '<button type="button" class="del" data-ai="list-pick" data-v="">Clear Cell</button>' : ""}</div></div>`;
}

/** The values a list cell offers. */
export function listValues(cell: keyof IdForm, classes: readonly ClassOption[]): readonly string[] {
  if (cell === "loc") return LOCATIONS.map((l) => l.name);
  if (cell === "type") return classes.map((c) => c.name);
  if (cell === "volt") return VOLTAGES;
  if (cell === "cond") return CONDUCTORS;
  return ["NO", "YES"];
}

/** A column's definition card. Tap anywhere to close. */
function defCard(d: NonNullable<SheetView["def"]>): string {
  const f = FIELD_DEFINITIONS[d.field];
  return `<div class="ai-sheet ac-listwrap" data-ai-sheet><div class="ac-defcard" style="left:${Math.round(d.left)}px;top:${Math.round(d.top)}px"><b>${esc(f.title)}</b>${esc(f.text)}</div></div>`;
}

export function creatorPage(v: SheetView): string {
  if (!v.isAdmin) return '<div class="note red">The Assessment Sheet needs an admin badge.</div>';
  const problems = v.problems.length
    ? `<div class="ac-problems">${v.problems
        .slice(0, 4)
        .map((p) => `<div class="note red ac-problem"><span>${esc(p.text)}</span><span class="ac-pbtns"><button type="button" data-ai="renumber" data-id="${esc(p.assetId)}">Renumber</button><button type="button" data-ai="drop" data-id="${esc(p.assetId)}">Drop</button></span></div>`)
        .join("")}${v.problems.length > 4 ? `<div class="note red">${v.problems.length - 4} more refused. Open the Outbox.</div>` : ""}</div>`
    : "";
  const body = v.rows.map((r) => row(v, r)).join("");
  // Open space under the last row: push the bottom rows up by hand. Nothing scrolls on its own (rev 6 section 3).
  return `<div class="ac-page">${bar(v)}${problems}<div class="ac-wrap" id="ac-wrap"><table class="ac-sheet${v.calcs ? " calcs" : ""}" style="zoom:${v.zoom}">${head(v)}<tbody>${body}</tbody></table><div class="ac-spacer"></div></div>${v.menu ? rowMenu(v.menu, v.showRemoved) : ""}${v.list ? listPop(v.list) : ""}${v.def ? defCard(v.def) : ""}</div>`;
}

// ---- Sheets ----

/** Copy Down To (rev 5 section 4): the same step, many rows, listed before they are added. */
export function downToSheet(v: { source: string; field: "pos" | "num"; from: string; to: string; ids: { id: string; exists: boolean }[] }): string {
  const make = v.ids.filter((x) => !x.exists);
  const skipped = v.ids.length - make.length;
  const what = v.field === "pos" ? "END Location" : "Equipment Number";
  const chips = v.ids.length
    ? `<div class="ai-chips">${v.ids.map((x) => `<span class="ai-chip${x.exists ? " skip" : ""}">${esc(x.id.replace(/^\d{2}-/, ""))}</span>`).join("")}</div>`
    : `<div class="note">${v.to ? `Counting up from ${esc(v.from)}, ${esc(v.to)} never comes. Up to 60 at a time.` : v.field === "pos" ? "Type the last one: D6, say. The letter stays." : "Type the last number: 22, say."}</div>`;
  return `<div class="ai-sheet" data-ai-sheet><div class="ai-card">
    <div class="ai-cardhd"><b>Copy Down To</b><button class="ai-tbtn" type="button" data-ai="close">Cancel</button></div>
    <div class="ai-annot">Copies row ${esc(v.source.replace(/^\d{2}-/, ""))} down, one row per ${what}. Voltage and Conductor carry. The four field answers come blank, to answer at each asset. Each row saves as you finish it.</div>
    <div class="ai-grid"><div class="ai-fld"><label>From</label><input value="${esc(v.from)}" disabled></div>
      <div class="ai-fld"><label for="ai-range-to">To ${what}</label><input id="ai-range-to" data-ai-range value="${esc(v.to)}" autocomplete="off" autocapitalize="characters" spellcheck="false"></div></div>
    ${chips}
    ${skipped ? `<div class="note">${skipped} already exist${skipped === 1 ? "s" : ""} and ${skipped === 1 ? "is" : "are"} skipped.</div>` : ""}
    <div class="ai-saves two"><button class="ai-sbtn" type="button" data-ai="close">Cancel</button><button class="ai-sbtn dark" type="button" data-ai="range-go"${make.length ? "" : " disabled"}>Add ${make.length} Row${make.length === 1 ? "" : "s"}</button></div>
  </div></div>`;
}

/**
 * Push To Assessment (was Bring In From Field Labels, BRING_IN_B3_WORDS.md; renamed ASSESSMENT_FLOW_B5_WORDS.md): the
 * "are you sure" step Robert asked for (10-27 9:13 PM). Opened from Field Labels. Nothing happens until Push.
 */
export function bringInSheet(v: {
  /** The room pushed (PUSH_BY_ROOM_B7_WORDS.md items 1 and 2). Null: the whole walk. */
  room?: string | null;
  count: number;
  already: number;
  waiting?: number;
  byRoom: { room: string; count: number }[];
  skipped: { room: string; gear: string; type: string }[];
  walked: boolean;
  remove?: readonly string[];
  restore?: readonly string[];
  /** Removals and put backs from rooms no longer in the walk: they ride along with this push (item 10). */
  goneRemove?: readonly string[];
  goneRestore?: readonly string[];
}): string {
  const rows = (n: number) => `${n} row${n === 1 ? "" : "s"}`;
  const room = v.room ?? null;
  const goneRemove = v.goneRemove ?? [];
  const goneRestore = v.goneRestore ?? [];
  const remove = v.remove ?? [];
  const restore = v.restore ?? [];
  const lists = (rm: readonly string[], rs: readonly string[]) =>
    `${rm.length ? `<div class="ai-birm"><b>${rm.length} To Remove</b>${rm.map((id) => `<span>${esc(id)}</span>`).join("")}</div>` : ""}${
      rs.length ? `<div class="ai-birs"><b>${rs.length} To Put Back</b>${rs.map((id) => `<span>${esc(id)}</span>`).join("")}</div>` : ""
    }`;
  // Removals on their own line, in red, before Push (ASSESSMENT_FLOW_B7_PART2_WORDS.md item 6, mockup screen 4).
  const carry = `${lists(remove, restore)}${goneRemove.length || goneRestore.length ? `<div class="ai-bihd">From Rooms No Longer In The Walk</div>${lists(goneRemove, goneRestore)}` : ""}`;
  const allRemove = remove.length + goneRemove.length;
  const allRestore = restore.length + goneRestore.length;
  const go = v.count + allRemove + allRestore;
  const goWords = [v.count ? `Push ${rows(v.count).replace(/^(\d+) r/, "$1 R")}` : "Push", allRemove ? `${allRemove} Remove` : "", allRestore ? `${allRestore} Put Back` : ""]
    .filter(Boolean)
    .join(" · ");
  const body = !v.walked
    ? `<div class="note">No walk on this iPad yet. Open Field Labels for this site once, then come back.</div>`
    : `<div class="ai-annot">From ${room ? `${esc(room)} in the walk` : "the walk on this iPad"}, to the Assessment Sheet. Each new row comes in with its tag, location, type, number and position. Voltage, conductor and the four answers stay blank. Each row saves when you fill them.${allRemove ? " Removed rows are marked removed on the sheet; their answers stay." : ""}</div>
    <div class="ai-bifacts"><b>${v.count}</b><span>new row${v.count === 1 ? "" : "s"}${room ? ` from ${esc(room)}` : ""}</span>${remove.length ? `<b>${remove.length}</b><span>to remove</span>` : ""}${restore.length ? `<b>${restore.length}</b><span>to put back, with their answers</span>` : ""}<b>${v.already - restore.length}</b><span>already on the sheet. They stay exactly as they are.</span>${v.waiting ? `<b>${v.waiting}</b><span>already brought in, waiting their turn.</span>` : ""}</div>
    ${room ? `<div class="ai-binote">Other rooms are not touched.</div>` : ""}
    ${carry}
    ${!room && v.byRoom.length ? `<div class="ai-bihd">New Rows By Room</div><div class="ai-birooms">${v.byRoom.map((r) => `<span class="ai-biroom">${esc(r.room)}<small>${rows(r.count)}</small></span>`).join("")}</div>` : ""}
    ${
      v.skipped.length
        ? `<div class="note">Can't push ${v.skipped.length}: ${v.skipped.map((x) => `${esc(x.room)} ${esc(x.gear)} is type ${esc(x.type)}`).join("; ")}, which has no Equipment Class. Add the class on the Lists page, then tap ${room ? `Push ${esc(room)}` : "Push To Assessment"} again.</div>`
        : ""
    }`;
  return `<div class="ai-sheet" data-ai-sheet><div class="ai-card">
    <div class="ai-cardhd"><b>${room ? `Push ${esc(room)} To Assessment` : "Push To Assessment"}</b></div>
    ${body}
    <div class="ai-saves two"><button class="ai-sbtn" type="button" data-ai="close">Cancel</button><button class="ai-sbtn dark" type="button" data-ai="bringin-go"${go ? "" : " disabled"}>${go ? goWords : "Nothing New To Push"}</button></div>
  </div></div>`;
}

/** The Remove confirm. It names what the remove touches. */
export function removeSheet(v: { assetId: string; warning: string }): string {
  return `<div class="ai-sheet" data-ai-sheet><div class="ai-card">
    <div class="ai-cardhd"><b>Remove ${esc(v.assetId.replace(/^\d{2}-/, ""))}?</b></div>
    <div class="ai-annot">${esc(v.warning)}</div>
    <div class="ai-saves two"><button class="ai-sbtn" type="button" data-ai="close">Cancel</button><button class="ai-sbtn red" type="button" data-ai="remove-go" data-key="${esc(v.assetId)}">Remove Row</button></div>
  </div></div>`;
}

